import { useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import { RESPUESTA_OTRO, type RespuestasDeLaEspera } from '../../../lib/onboardingMarca/marca';
import { leerEstado } from '../../../lib/onboardingMarca/estado';
import { detectarPlataforma } from '../../../lib/onboardingMarca/link';
import { LETRAS_DE_OPCION, MAX_OTRO, PREGUNTAS, preguntaSegunElLink, type Pregunta } from '../../../lib/onboardingMarca/preguntas';

/**
 * Las preguntas que no se pueden deducir del sitio, respondidas MIENTRAS Atlas
 * analiza (ADR-2026-09-28-onboarding-v2-etapa-1): así la espera no se siente. Una a la
 * vez, con teclas A/B/C/D/E. Son obligatorias y no se saltean: sin todas no se sigue
 * (ADR-2026-09-29-onboarding-v2-accion-abajo). Cada una tiene "Otro", que abre un campo
 * para escribir la respuesta (ADR-2026-09-29-onboarding-v2-preguntas-con-otro).
 *
 * Desde ADR-2026-10-02-onboarding-v2-preguntas-con-cumbre las hace Cumbre en su propio paso
 * (`PreguntasDeMarca`); el tablero sólo las muestra si llegó sin responderlas.
 */
const LETRAS = LETRAS_DE_OPCION;

export function PreguntasDeEspera(p: {
  respuestas: RespuestasDeLaEspera;
  /** Las que se le hacen (`preguntasDelOnboarding`): sin "qué crear primero" si ya generó. */
  preguntas?: Pregunta[];
  onResponder: (parcial: RespuestasDeLaEspera) => void;
  /** Mientras Atlas analiza son "mientras tanto"; después, lo que falta para seguir. */
  analizando: boolean;
}) {
  const [elegida, setElegida] = useState<string | null>(null);
  // Si el link es de una tienda en Tiendanube o Shopify, esa opción se marca como sugerida
  // (sin darla por respondida: la pregunta sigue siendo obligatoria).
  const link = useMemo(() => leerEstado(null)?.link ?? null, []);
  const sugerida = useMemo(() => {
    const plataforma = link ? detectarPlataforma(link) : null;
    return plataforma === 'tiendanube' || plataforma === 'shopify' ? plataforma : null;
  }, [link]);
  const [escribiendo, setEscribiendo] = useState(false);
  const [texto, setTexto] = useState('');
  const inputOtro = useRef<HTMLInputElement>(null);
  const preguntas = p.preguntas ?? PREGUNTAS;
  const indice = preguntas.findIndex((q) => !p.respuestas[q.clave]);
  const pregunta = indice >= 0 ? preguntaSegunElLink(preguntas[indice], Boolean(link)) : null;
  // "Otro" es siempre la última letra, después de las opciones de la pregunta.
  const letraOtro = pregunta ? LETRAS[pregunta.opciones.length] : '';

  const pasar = (parcial: RespuestasDeLaEspera, id: string) => {
    setElegida(id);
    // Un instante con la opción marcada antes de pasar a la siguiente.
    window.setTimeout(() => {
      p.onResponder(parcial);
      setElegida(null);
      setEscribiendo(false);
      setTexto('');
    }, 280);
  };

  const responder = (id: string) => {
    if (!pregunta || elegida) return;
    setEscribiendo(false);
    pasar({ [pregunta.clave]: id } as RespuestasDeLaEspera, id);
  };

  const abrirOtro = () => {
    if (!pregunta || elegida) return;
    setEscribiendo(true);
    window.setTimeout(() => inputOtro.current?.focus(), 0);
  };

  const enviarOtro = (e: FormEvent) => {
    e.preventDefault();
    const limpio = texto.trim();
    if (!pregunta || elegida || !limpio) return;
    // `otros` va entero: la pantalla mezcla las respuestas con un spread de un nivel.
    pasar(
      { [pregunta.clave]: RESPUESTA_OTRO, otros: { ...p.respuestas.otros, [pregunta.clave]: limpio } } as RespuestasDeLaEspera,
      RESPUESTA_OTRO,
    );
  };

  useEffect(() => {
    if (!pregunta) return;
    const onKey = (e: KeyboardEvent) => {
      const t = e.target as HTMLElement | null;
      if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.tagName === 'SELECT' || t.isContentEditable)) return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      const letra = e.key.toUpperCase();
      if (letra === letraOtro) {
        e.preventDefault();
        abrirOtro();
        return;
      }
      const i = LETRAS.indexOf(letra);
      const opcion = i >= 0 ? pregunta.opciones[i] : undefined;
      if (opcion) {
        e.preventDefault();
        responder(opcion.id);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });

  const claseOpcion = (activa: boolean) =>
    `inline-flex items-center gap-2 rounded-[9px] border px-2.5 py-1.5 text-[12.5px] font-semibold transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-brand-primary ${
      activa ? 'border-brand-primary bg-brand-primary text-white' : 'border-[rgb(var(--brand-primary-rgb)_/_0.17)] bg-white text-brand-black hover:border-brand-primary'
    }`;

  return (
    <div id="om-preguntas" className="grid scroll-mt-[76px] gap-2.5 rounded-[14px] border border-[rgb(var(--brand-primary-rgb)_/_0.17)] bg-[rgb(var(--brand-primary-rgb)_/_0.05)] px-4 py-3.5 text-brand-black" aria-live="polite">
      <div className="flex justify-between text-[12.5px] font-semibold text-brand-primary">
        <span>{p.analizando ? 'Mientras tanto' : 'Antes de seguir'}</span>
        {pregunta && (
          <span>
            {indice + 1} de {preguntas.length}
          </span>
        )}
      </div>
      {pregunta ? (
        <div key={pregunta.clave} className="om-llega grid gap-2.5">
          <p className="text-[15px] font-bold tracking-[-0.01em]">{pregunta.titulo}</p>
          <div className="flex flex-wrap gap-1.5" role="radiogroup" aria-label={pregunta.titulo}>
            {pregunta.opciones.map((o, i) => (
              <button
                key={o.id}
                type="button"
                role="radio"
                aria-checked={elegida === o.id}
                onClick={() => responder(o.id)}
                className={claseOpcion(elegida === o.id)}
              >
                <kbd className="rounded bg-[rgb(var(--brand-primary-rgb)_/_0.1)] px-1 text-[10px] font-semibold text-brand-primary">{LETRAS[i]}</kbd>
                {o.label}
                {pregunta.clave === 'canal' && o.id === sugerida && elegida !== o.id && (
                  <span className="rounded-full bg-[#ECFDF5] px-1.5 text-[10.5px] font-semibold text-[#065F46]">por tu link</span>
                )}
              </button>
            ))}
            <button
              type="button"
              role="radio"
              aria-checked={escribiendo || elegida === RESPUESTA_OTRO}
              aria-expanded={escribiendo}
              onClick={abrirOtro}
              className={claseOpcion(escribiendo || elegida === RESPUESTA_OTRO)}
            >
              <kbd className="rounded bg-[rgb(var(--brand-primary-rgb)_/_0.1)] px-1 text-[10px] font-semibold text-brand-primary">{letraOtro}</kbd>
              Otro
            </button>
          </div>
          {escribiendo && (
            <form onSubmit={enviarOtro} className="om-llega flex gap-1.5">
              <label htmlFor={`om-otro-${pregunta.clave}`} className="sr-only">
                {pregunta.titulo} Otro
              </label>
              <input
                id={`om-otro-${pregunta.clave}`}
                ref={inputOtro}
                value={texto}
                onChange={(e) => setTexto(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Escape') {
                    setEscribiendo(false);
                    setTexto('');
                  }
                }}
                maxLength={MAX_OTRO}
                placeholder={pregunta.ejemploOtro}
                className="min-w-0 flex-1 rounded-[9px] border border-[rgb(var(--brand-primary-rgb)_/_0.17)] bg-white px-3 py-1.5 text-[12.5px] font-semibold text-[#18181B] outline-none placeholder:font-normal placeholder:text-[#9F9FA9] focus:border-brand-primary"
              />
              <button
                type="submit"
                disabled={!texto.trim() || Boolean(elegida)}
                className="shrink-0 rounded-[9px] bg-brand-primary px-3 text-[13px] font-bold text-white disabled:bg-[rgb(var(--brand-primary-rgb)_/_0.17)] disabled:text-[#71717B]"
              >
                Listo
              </button>
            </form>
          )}
        </div>
      ) : (
        <p className="om-llega text-[15px] font-bold">Listo, con eso alcanza. Seguimos con tu marca.</p>
      )}
    </div>
  );
}
